/**
 * Side-by-side: ClimatePlans county-summed sectors (+ CARB overlays) vs CARB Figure 4.
 * Unified legend, aligned stack order, shared y-scale (metric tons internally; axis in MMT).
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { ChartData, ChartOptions } from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  CARB_MT_ELECTRIC_POWER,
  CARB_MT_HIGH_GWP,
  CARB_MT_INDUSTRIAL,
  carbFigure4SectorMt,
  type CarbFigure4SectorKey,
} from '../../data/carbGhgFigure4Reference';
import { PUBLICATION_ANALYSIS_CHART_IDS } from '../../config/publicationAnalysisChartIds';
import {
  PUBLICATION_CARB_COMPARE_YEAR_MAX,
  PUBLICATION_GHG_CHART_YEAR_MIN,
} from '../../config/publicationGhgChartYears';
import { ChartPngPageIdOverrideContext, useChartPngRegistryActions } from '../../contexts/ChartPngRegistryContext';
import { PublicationFigureDownloads } from '../publication/PublicationFigureDownloads';
import {
  fetchCaliforniaStateGhgBySector,
  type StateSectorYearRow,
} from '../../services/stateGhgInventorySectorService';
import { downloadDataAsCSV, downloadDomAsPNG, formatFilename } from '../../utils/downloadUtils';
import { ANALYSIS_SECTOR_GHG_PALETTE as GHG } from '../../theme/navChartPalette';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const PAL = {
  transportCarb: GHG.transportation,
  electric: GHG.electricity,
  electricUndetermined: GHG.undeterminedElectricity,
  agriculture: GHG.agriculture,
  offroad: GHG.offroad,
  fuelsBuildings: GHG.naturalGas,
  waste: GHG.waste,
  industrialCarb: GHG.industrialCarb,
  highGwpCarb: GHG.highGwpCarb,
} as const;

/** Bottom → top stack order (Chart.js: first dataset = bottom layer). */
const COMPARISON_LAYERS: {
  id: string;
  legendLabel: string;
  border: string;
  fill: string;
}[] = [
  {
    id: 'transportation',
    legendLabel: 'Transportation',
    border: PAL.transportCarb.border,
    fill: PAL.transportCarb.fill,
  },
  {
    id: 'natural_gas',
    legendLabel: 'Natural gas (residential + commercial)',
    border: PAL.fuelsBuildings.border,
    fill: PAL.fuelsBuildings.fill,
  },
  {
    id: 'agriculture',
    legendLabel: 'Agriculture',
    border: PAL.agriculture.border,
    fill: PAL.agriculture.fill,
  },
  {
    id: 'offroad',
    legendLabel: 'Off-road',
    border: PAL.offroad.border,
    fill: PAL.offroad.fill,
  },
  {
    id: 'waste',
    legendLabel: 'Waste',
    border: PAL.waste.border,
    fill: PAL.waste.fill,
  },
  {
    id: 'electricity_res',
    legendLabel: 'Electricity (residential + commercial)',
    border: PAL.electric.border,
    fill: PAL.electric.fill,
  },
  {
    id: 'electricity_other',
    legendLabel: 'Electricity (other)',
    border: PAL.electricUndetermined.border,
    fill: PAL.electricUndetermined.fill,
  },
  {
    id: 'industrial',
    legendLabel: 'Industrial (not in ClimatePlans inventory)',
    border: PAL.industrialCarb.border,
    fill: PAL.industrialCarb.fill,
  },
  {
    id: 'high_gwp',
    legendLabel: 'High GWP (not in ClimatePlans inventory)',
    border: PAL.highGwpCarb.border,
    fill: PAL.highGwpCarb.fill,
  },
];

type OurStackField = keyof Pick<
  StateSectorYearRow,
  'offroad_emissions' | 'ng_total_emissions' | 'waste_emissions' | 'agriculture_emissions'
>;

const OUR_STACK_AFTER_TRANSPORT: { field: OurStackField }[] = [
  { field: 'ng_total_emissions' },
  { field: 'agriculture_emissions' },
  { field: 'offroad_emissions' },
  { field: 'waste_emissions' },
];

function sumTransportMt(r: StateSectorYearRow | undefined): number {
  if (!r) return 0;
  const a = r.ldv_emissions;
  const b = r.hdv_emissions;
  return (
    (a != null && Number.isFinite(a) ? a : 0) + (b != null && Number.isFinite(b) ? b : 0)
  );
}

function undeterminedElectricityMt(year: number, r: StateSectorYearRow | undefined): number {
  const carb = CARB_MT_ELECTRIC_POWER[year];
  if (carb == null || !Number.isFinite(carb)) return 0;
  const ours = r?.elec_total_emissions;
  const o = ours != null && Number.isFinite(ours) ? ours : 0;
  return Math.max(0, carb - o);
}

function rowByYear(rows: StateSectorYearRow[]): Map<number, StateSectorYearRow> {
  const m = new Map<number, StateSectorYearRow>();
  for (const r of rows) m.set(r.year, r);
  return m;
}

function yTickMillionMt(value: string | number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return (n / 1e6).toFixed(1);
}

function maxStackedTotal(years: number[], perYear: (y: number) => number): number {
  let m = 0;
  for (const y of years) {
    const t = perYear(y);
    if (Number.isFinite(t)) m = Math.max(m, t);
  }
  return m;
}

function sharedYAxisScale(peakMt: number): { max: number; stepSize: number } {
  const raw = Math.max(peakMt, 1) * 1.06;
  const roughStep = raw / 7;
  const pow10 = 10 ** Math.floor(Math.log10(Math.max(roughStep, 1)));
  const n = roughStep / pow10;
  const niceUnit = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  const stepSize = niceUnit * pow10;
  const max = Math.ceil(raw / stepSize) * stepSize;
  return { max, stepSize };
}

function SharedLegend() {
  const topFirst = [...COMPARISON_LAYERS].reverse();
  return (
    <ul className="publication-carb-shared-legend" aria-label="Emission categories">
      {topFirst.map((layer) => (
        <li key={layer.id}>
          <span
            className="publication-carb-shared-legend-swatch"
            style={{ backgroundColor: layer.fill, borderColor: layer.border }}
          />
          <span className="publication-carb-shared-legend-label">{layer.legendLabel}</span>
        </li>
      ))}
    </ul>
  );
}

type ComparisonChartsProps = {
  leftData: ChartData<'line'>;
  rightData: ChartData<'line'>;
  chartOptions: ChartOptions<'line'>;
};

function ComparisonCharts({ leftData, rightData, chartOptions }: ComparisonChartsProps) {
  return (
    <div className="publication-carb-comparison-charts">
      <div className="publication-carb-comparison-charts__col">
        <p className="publication-carb-comparison-charts__panel-title">ClimatePlans (counties summed)</p>
        <div className="publication-ghg-carb-chart-canvas">
          <Line data={leftData} options={chartOptions} />
        </div>
      </div>
      <div className="publication-carb-comparison-charts__col">
        <p className="publication-carb-comparison-charts__panel-title">CARB statewide (reference)</p>
        <div className="publication-ghg-carb-chart-canvas">
          <Line data={rightData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
}

function leftLayerSeries(
  layerId: string,
  years: number[],
  byYear: Map<number, StateSectorYearRow>
): number[] {
  const num = (v: number | null | undefined) => (v != null && Number.isFinite(v) ? v : 0);
  switch (layerId) {
    case 'transportation':
      return years.map((y) => num(sumTransportMt(byYear.get(y))));
    case 'natural_gas':
      return years.map((y) => num(byYear.get(y)?.ng_total_emissions));
    case 'agriculture':
      return years.map((y) => num(byYear.get(y)?.agriculture_emissions));
    case 'offroad':
      return years.map((y) => num(byYear.get(y)?.offroad_emissions));
    case 'waste':
      return years.map((y) => num(byYear.get(y)?.waste_emissions));
    case 'electricity_res':
      return years.map((y) => num(byYear.get(y)?.elec_total_emissions));
    case 'electricity_other':
      return years.map((y) => undeterminedElectricityMt(y, byYear.get(y)));
    case 'industrial':
      return years.map((y) => num(CARB_MT_INDUSTRIAL[y]));
    case 'high_gwp':
      return years.map((y) => num(CARB_MT_HIGH_GWP[y]));
    default:
      return years.map(() => 0);
  }
}

function rightLayerSeries(layerId: string, years: number[]): number[] {
  const carb = (sector: CarbFigure4SectorKey, y: number) => carbFigure4SectorMt(sector, y) ?? 0;
  switch (layerId) {
    case 'transportation':
      return years.map((y) => carb('Transportation', y));
    case 'natural_gas':
      return years.map((y) => carb('Commercial & Residential', y));
    case 'agriculture':
      return years.map((y) => carb('Agriculture', y));
    case 'offroad':
      return years.map(() => 0);
    case 'waste':
      return years.map((y) => carb('Recycling & Waste', y));
    case 'electricity_res':
      return years.map((y) => carb('Electric Power', y));
    case 'electricity_other':
      return years.map(() => 0);
    case 'industrial':
      return years.map((y) => carb('Industrial', y));
    case 'high_gwp':
      return years.map((y) => carb('High GWP', y));
    default:
      return years.map(() => 0);
  }
}

export const CountyInventoryVsCarbSectorCharts: React.FC = () => {
  const figureRef = useRef<HTMLDivElement>(null);
  const methodologyRef = useRef<HTMLDetailsElement>(null);
  const pngPageOverride = useContext(ChartPngPageIdOverrideContext);
  const pngPageId = pngPageOverride ?? 'statewide_analysis';
  const { register: registerPng, unregister: unregisterPng } = useChartPngRegistryActions();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<Awaited<ReturnType<typeof fetchCaliforniaStateGhgBySector>> | null>(
    null
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await fetchCaliforniaStateGhgBySector({
      yearMin: PUBLICATION_GHG_CHART_YEAR_MIN,
      yearMax: PUBLICATION_CARB_COMPARE_YEAR_MAX,
    });
    if (res.error) {
      setError(res.error);
      setPayload(null);
    } else {
      setPayload(res);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const years = useMemo(() => {
    const lo = PUBLICATION_GHG_CHART_YEAR_MIN;
    const hi = PUBLICATION_CARB_COMPARE_YEAR_MAX;
    return (payload?.years ?? [])
      .filter((y) => y >= lo && y <= hi)
      .slice()
      .sort((a, b) => a - b);
  }, [payload]);
  const byYear = useMemo(() => (payload ? rowByYear(payload.rows) : new Map()), [payload]);

  const yAxisScale = useMemo(() => {
    if (years.length === 0) return { max: 1, stepSize: 1 };
    const ours = maxStackedTotal(years, (y) => {
      const r = byYear.get(y);
      let s = sumTransportMt(r);
      for (const { field } of OUR_STACK_AFTER_TRANSPORT) {
        const v = r?.[field];
        if (v != null && Number.isFinite(v)) s += v;
      }
      const elec = r?.elec_total_emissions;
      if (elec != null && Number.isFinite(elec)) s += elec;
      s += undeterminedElectricityMt(y, r);
      const ind = CARB_MT_INDUSTRIAL[y];
      const hg = CARB_MT_HIGH_GWP[y];
      if (ind != null) s += ind;
      if (hg != null) s += hg;
      return s;
    });
    const carb = maxStackedTotal(years, (y) => {
      let s = 0;
      for (const layer of COMPARISON_LAYERS) {
        const series = rightLayerSeries(layer.id, [y]);
        s += series[0] ?? 0;
      }
      return s;
    });
    return sharedYAxisScale(Math.max(ours, carb, 1));
  }, [years, byYear]);

  const leftChartData = useMemo((): ChartData<'line'> | null => {
    if (!payload || years.length === 0) return null;
    const labels = years.map(String);
    const datasets = COMPARISON_LAYERS.map((layer) => ({
      label: layer.legendLabel,
      data: leftLayerSeries(layer.id, years, byYear),
      borderColor: layer.border,
      backgroundColor: layer.fill,
      fill: true,
      tension: 0.12,
      pointRadius: 0,
      pointHitRadius: 6,
      borderWidth: 1,
      stack: 'cp',
    }));
    return { labels, datasets };
  }, [payload, years, byYear]);

  const rightChartData = useMemo((): ChartData<'line'> | null => {
    if (years.length === 0) return null;
    const labels = years.map(String);
    const datasets = COMPARISON_LAYERS.map((layer) => ({
      label: layer.legendLabel,
      data: rightLayerSeries(layer.id, years),
      borderColor: layer.border,
      backgroundColor: layer.fill,
      fill: true,
      tension: 0.12,
      pointRadius: 0,
      pointHitRadius: 6,
      borderWidth: 1,
      stack: 'carb',
    }));
    return { labels, datasets };
  }, [years]);

  const chartOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: {
          itemSort: (a, b) => b.datasetIndex - a.datasetIndex,
          callbacks: {
            title: (items) => (items[0]?.label ? `Year ${items[0].label}` : ''),
            label: (ctx) => {
              const v = ctx.parsed.y;
              const lab = ctx.dataset.label || '';
              if (v == null || !Number.isFinite(v)) return `${lab}: —`;
              const mmt = v / 1e6;
              return `${lab}: ${mmt.toFixed(2)} MMT (${Math.round(v).toLocaleString()} t)`;
            },
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: 'Year' },
          ticks: { maxRotation: 0, font: { size: 10 } },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          min: 0,
          max: yAxisScale.max,
          title: { display: true, text: 'Million metric tons CO₂e' },
          ticks: {
            font: { size: 10 },
            stepSize: yAxisScale.stepSize,
            callback: (value) => yTickMillionMt(value),
          },
        },
      },
    }),
    [yAxisScale]
  );

  const exportCsv = useCallback(() => {
    if (!payload || years.length === 0) return;
    const rows = years.map((y) => {
      const r = byYear.get(y);
      return {
        year: y,
        ldv_emissions: r?.ldv_emissions ?? '',
        hdv_emissions: r?.hdv_emissions ?? '',
        transport_ldv_hdv_sum_mt: sumTransportMt(r) || '',
        offroad_emissions: r?.offroad_emissions ?? '',
        elec_total_emissions: r?.elec_total_emissions ?? '',
        undetermined_electricity_mt: undeterminedElectricityMt(y, r) || '',
        carb_electric_power_mt: CARB_MT_ELECTRIC_POWER[y] ?? '',
        ng_total_emissions: r?.ng_total_emissions ?? '',
        waste_emissions: r?.waste_emissions ?? '',
        agriculture_emissions: r?.agriculture_emissions ?? '',
        carb_industrial_overlay_mt: CARB_MT_INDUSTRIAL[y] ?? '',
        carb_high_gwp_overlay_mt: CARB_MT_HIGH_GWP[y] ?? '',
        sum_county_total_emissions: r?.total_emissions ?? '',
      };
    });
    downloadDataAsCSV(rows, formatFilename('California', 'climateplans_vs_carb_sectors'));
  }, [payload, years, byYear]);

  const handleDownloadPng = useCallback(() => {
    return downloadDomAsPNG(figureRef.current, formatFilename('California', 'climateplans_vs_carb_figure'));
  }, []);

  const openMethodology = useCallback(() => {
    const el = methodologyRef.current;
    if (el) {
      el.open = true;
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  useEffect(() => {
    const id = PUBLICATION_ANALYSIS_CHART_IDS.vsCarb;
    const label = 'Statewide emissions comparison';
    registerPng(id, label, () => void handleDownloadPng(), pngPageId);
    return () => unregisterPng(id);
  }, [registerPng, unregisterPng, handleDownloadPng, pngPageId]);

  if (loading) {
    return (
      <p className="publication-county-ghg-status" role="status">
        Loading county inventory and CARB reference…
      </p>
    );
  }

  if (error) {
    return <p className="publication-scatter-error">Could not load inventory: {error}</p>;
  }

  if (!leftChartData || !rightChartData) {
    return (
      <p className="publication-scatter-placeholder">
        No county rows to display. Run <code>npm run populate-ghg-inventory</code> for California counties.
      </p>
    );
  }

  return (
    <div ref={figureRef} className="publication-figure-frame publication-carb-comparison-root">
      <header className="publication-carb-comparison-header">
        <h2 className="publication-carb-comparison-title">Statewide emissions comparison</h2>
        <p className="publication-carb-comparison-subtitle">ClimatePlans vs. CARB inventory</p>
        <p className="publication-carb-comparison-purpose">
          Comparison shows strong alignment with CARB totals, with differences driven by sector definitions and
          electricity accounting.
        </p>
      </header>

      <ComparisonCharts leftData={leftChartData} rightData={rightChartData} chartOptions={chartOptions} />

      <SharedLegend />

      <PublicationFigureDownloads
        onCsv={exportCsv}
        onPng={handleDownloadPng}
        ariaLabel="Download statewide comparison figure"
      />

      <p className="publication-carb-comparison-source">
        Source:{' '}
        <a href="https://ww2.arb.ca.gov/ghg-inventory-data" target="_blank" rel="noreferrer">
          CARB GHG Inventory (Figure 4)
        </a>
        .{' '}
        <button type="button" className="publication-carb-comparison-method-link" onClick={openMethodology}>
          View methodology
        </button>
      </p>

      <details ref={methodologyRef} className="publication-carb-methodology" id="carb-comparison-methodology">
        <summary className="publication-carb-methodology-summary">How this comparison is constructed</summary>
        <div className="publication-carb-methodology-body">
          <p>
            <strong>ClimatePlans (left):</strong> County rows in <code>ghg_inventory_precalculated</code> are summed
            by year for California counties. Stack order matches the legend (transportation through high GWP).{' '}
            <em>Electricity (other)</em> is max(0, CARB statewide electric power minus county-summed electricity) so the
            stacked total aligns with CARB&rsquo;s electric-power total while showing inventoried grid-related
            emissions separately. <em>Industrial</em> and <em>High GWP</em> layers use CARB statewide totals as
            overlays (not separately inventoried in ClimatePlans counties).
          </p>
          <p>
            <strong>CARB (right):</strong> Economic-sector totals from CARB Figure 4 (2000–2023 data).{' '}
            <em>Natural gas (residential + commercial)</em> in the legend corresponds to CARB &ldquo;Commercial &amp;
            residential.&rdquo; <em>Electricity (residential + commercial)</em> maps to CARB &ldquo;Electric
            power&rdquo; (CARB has no separate &ldquo;electricity other&rdquo; band in this figure). Off-road is zero
            on the right because Figure 4 does not break it out.
          </p>
          <p>
            For agriculture and other sector gaps, see GHG populate methodology notes and{' '}
            <code>docs/GHG_INVENTORY_CALCULATION_SUMMARY.md</code>. Y-axis uses the same scale for both charts (values
            in metric tons; ticks shown as million metric tons).
          </p>
        </div>
      </details>
    </div>
  );
};

export default CountyInventoryVsCarbSectorCharts;
